import React from 'react';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import { PrimaryButton } from '@/styles/blocks';
import { TabCard } from '../../blocks';
import { css } from '@emotion/react';
import { DEFAULT_HOURS_OPTION } from './constants';
import { storeLabel } from './utils';
import { useStores } from './hooks';
import FirstLocationForm from './FirstLocationForm';
import HoursSection from './HoursSection';
import LocationForm from './LocationForm';
import { BlockingLink, BlockingNote, Divider, FieldLabel, Panel, SaveRow, Toolbar, ValidationNote } from './blocks';

const cardPadding = css`
    padding: 2rem;
`;

/**
 * Warehouse and store locations: pick a location (or the client's default hours), edit it, save.
 *
 * `bare` drops the TabCard surface for callers that already provide one, same contract as
 * CustomTags, TariffTable and the document panels.
 */
const Stores = ({ currentClient, viewOnly, bare = false }) => {
    const { state, callbacks } = useStores({ currentClient, viewOnly });
    const {
        stores,
        chosenStore,
        chosen,
        validationMessage,
        isFirstLocation,
        blocking,
        canSubmitSelected,
        canSaveChanges,
    } = state;

    const Surface = bare ? React.Fragment : TabCard;
    const surfaceProps = bare ? {} : { css: cardPadding };

    // The hours heading names whichever record is being edited.
    const hoursTitle = chosen.location_name ? `Hours for ${chosen.location_name}` : 'Default Hours';

    return (
        <Surface {...surfaceProps}>
            <Panel>
                {!!stores.length && (
                    <Toolbar>
                        <div>
                            <FieldLabel>Select location</FieldLabel>
                            <FormControl
                                variant="outlined"
                                size="small"
                                css={css`
                                    min-width: 240px;
                                `}
                            >
                                <Select
                                    value={Number.isInteger(chosenStore) ? chosenStore : DEFAULT_HOURS_OPTION}
                                    onChange={(e) =>
                                        callbacks.selectStore(
                                            e.target.value === DEFAULT_HOURS_OPTION ? null : e.target.value
                                        )
                                    }
                                >
                                    <MenuItem value={DEFAULT_HOURS_OPTION}>Default Hours</MenuItem>
                                    {stores.map((store, idx) => (
                                        <MenuItem value={idx} key={store.location_id || `new-${idx}`}>
                                            {storeLabel(store)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {!viewOnly && (
                            <>
                                <PrimaryButton onClick={callbacks.addNewLocation}>Add a New Location</PrimaryButton>
                                {chosenStore !== null && (
                                    <PrimaryButton red onClick={callbacks.removeChosenLocation}>
                                        Remove Location
                                    </PrimaryButton>
                                )}
                            </>
                        )}
                    </Toolbar>
                )}

                {isFirstLocation && !viewOnly && <FirstLocationForm state={state} callbacks={callbacks} />}

                {!!stores.length && chosenStore !== null && chosenStore >= 0 && (
                    <LocationForm state={state} callbacks={callbacks} />
                )}

                <HoursSection chosen={chosen} viewOnly={viewOnly} title={hoursTitle} callbacks={callbacks} />

                {!viewOnly && (
                    <>
                        <Divider />
                        {/*
                            Name what is blocking the save and let the user jump straight to it —
                            a disabled button with no explanation is worse than the bug it guards.
                        */}
                        {blocking.length > 0 && (
                            <BlockingNote>
                                <strong>Finish these locations before saving:</strong>
                                <ul>
                                    {blocking.map((item) => (
                                        <li key={item.idx}>
                                            <BlockingLink type="button" onClick={() => callbacks.selectStore(item.idx)}>
                                                {item.name}
                                            </BlockingLink>{' '}
                                            — missing {item.missing.join(', ')}
                                        </li>
                                    ))}
                                </ul>
                            </BlockingNote>
                        )}
                        <SaveRow>
                            {isFirstLocation ? (
                                <PrimaryButton disabled={!canSubmitSelected} onClick={callbacks.submitFirstLocation}>
                                    Save First Location
                                </PrimaryButton>
                            ) : (
                                <PrimaryButton disabled={!canSaveChanges} onClick={callbacks.saveClient}>
                                    Save Changes
                                </PrimaryButton>
                            )}
                            {validationMessage && <ValidationNote>{validationMessage}</ValidationNote>}
                        </SaveRow>
                    </>
                )}
            </Panel>
        </Surface>
    );
};

export default Stores;
